import { useNavigate } from 'react-router-dom';
import { useAthleteStore } from '@/store';
import { useAuthStore } from '@/store/authStore';
import { Users, Plus, Star, Activity, LogOut } from 'lucide-react';
import { TextureButton } from '@/components/ui/TextureButton';

export function ParentProfile() {
    const navigate = useNavigate();
    const { athletes, activeId, setActiveId } = useAthleteStore();
    const { account, logout } = useAuthStore();

    const handleSwitch = async (id: string) => {
        await setActiveId(id);
    };

    return (
        <div className="min-h-screen bg-bg pb-32 animate-fade-in text-text-primary">
            <div className="max-w-4xl mx-auto pt-6 md:pt-10 px-4">

                {/* Header Section */}
                <div className="flex flex-col md:flex-row gap-6 items-start mb-8">
                    <div className="w-24 h-24 rounded-full bg-gradient-to-tr from-blue-500 to-bg-surface2 p-[2px]">
                        <div className="w-full h-full rounded-full border-[3px] border-bg bg-bg-elevated flex items-center justify-center text-3xl">
                            {account?.role === 'parent' ? '👨‍👩‍👧' : '👨‍👦'}
                        </div>
                    </div>
                    <div className="flex-1 w-full text-center md:text-left">
                        <div className="flex flex-col md:flex-row items-center md:items-start gap-4 mb-4">
                            <h1 className="text-xl md:text-2xl font-bold text-white flex items-center gap-2">
                                Parent Dashboard
                            </h1>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => navigate('/profiles')}
                                    className="glass-button p-2 rounded-lg text-text-primary hover:text-white"
                                    title="Switch Athlete Profile"
                                >
                                    <Users size={18} />
                                </button>
                                <button
                                    onClick={logout}
                                    className="glass-button p-2 rounded-lg text-red-500/80 hover:text-red-500"
                                    title="Logout"
                                >
                                    <LogOut size={18} />
                                </button>
                            </div>
                        </div>
                        <p className="text-text-secondary text-sm md:text-base max-w-lg">
                            Manage your athletes, monitor their progress, and oversee their training schedule.
                        </p>
                    </div>
                </div>

                {/* Subscriptions / Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                    <div className="p-4 rounded-xl bg-bg-surface border border-white/5">
                        <div className="text-2xl font-bold text-white mb-1">{athletes.length}</div>
                        <div className="text-xs font-bold uppercase tracking-widest text-text-muted">Athletes</div>
                    </div>
                    <div className="p-4 rounded-xl bg-bg-surface border border-white/5">
                        <div className="text-2xl font-bold text-white mb-1">PRO</div>
                        <div className="text-xs font-bold uppercase tracking-widest text-text-muted">Plan</div>
                    </div>
                    <div className="p-4 rounded-xl bg-bg-surface border border-white/5">
                        <div className="text-2xl font-bold text-gold-400 mb-1">45h</div>
                        <div className="text-xs font-bold uppercase tracking-widest text-text-muted">Total Activity</div>
                    </div>
                    <div className="p-4 rounded-xl bg-bg-surface border border-white/5">
                        <div className="text-2xl font-bold text-blue-400 mb-1">Active</div>
                        <div className="text-xs font-bold uppercase tracking-widest text-text-muted">Status</div>
                    </div>
                </div>

                {/* My Athletes List */}
                <div className="mb-6 flex justify-between items-center">
                    <h2 className="text-lg font-bold text-white flex items-center gap-2">
                        <Users size={18} className="text-blue-400" /> My Athletes
                    </h2>
                    <TextureButton onClick={() => navigate('/create-profile')} className="py-2 px-4 text-xs font-bold whitespace-nowrap !bg-blue-600">
                        <Plus size={14} className="inline-block mr-1" /> Add Athlete
                    </TextureButton>
                </div>

                <div className="space-y-4">
                    {athletes.map(a => (
                        <div key={a.id} className={`p-4 rounded-2xl border transition-all ${a.id === activeId ? 'bg-blue-500/10 border-blue-500/50' : 'bg-bg-surface border-white/10'}`}>
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0 bg-black/40 flex items-center justify-center text-xl">
                                    {a.avatar || 'U'}
                                </div>
                                <div className="flex-1">
                                    <h3 className="font-bold text-white flex items-center gap-2">
                                        {a.name}
                                        {a.id === activeId && <span className="px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-400 text-[10px] uppercase tracking-wider">Active</span>}
                                    </h3>
                                    <div className="text-xs text-text-muted flex items-center gap-3 mt-1">
                                        <span className="flex items-center gap-1"><Star size={12} className="text-gold-500" /> {a.level}</span>
                                        <span className="flex items-center gap-1"><Activity size={12} className="text-emerald-500" /> {a.experienceYears}y exp</span>
                                    </div>
                                </div>
                                <div>
                                    {a.id !== activeId && (
                                        <button onClick={() => handleSwitch(a.id)} className="px-4 py-2 rounded-lg bg-white/5 text-xs font-bold text-white hover:bg-white/10 transition-colors">
                                            Switch
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                    {athletes.length === 0 && (
                        <div className="py-12 text-center text-text-muted rounded-2xl border border-dashed border-white/20">
                            <Users className="w-12 h-12 mx-auto mb-3 opacity-20" />
                            <p>No athletes found. Add a child to begin tracking their journey.</p>
                        </div>
                    )}
                </div>

            </div>
        </div>
    );
}
